import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

const emptyForm = {
  profile_name: '', host: '', port: 587, secure: false,
  username: '', password: '', from_email: '', from_name: '',
};

function AdminMailSettings() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [testEmailFor, setTestEmailFor] = useState(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testing, setTesting] = useState(false);

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) {
        setError('Authentication required.');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${apiUrl}/api/admin/mail-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setProfiles(response.data.profiles);
      } else {
        setError(response.data.message || 'Failed to fetch mail settings.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching mail settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  const startAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const startEdit = (p) => {
    setForm({
      profile_name: p.profile_name, host: p.host, port: p.port, secure: !!p.secure,
      username: p.username, password: '', from_email: p.from_email || '', from_name: p.from_name || '',
    });
    setEditingId(p.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const token = getToken();
      const url = editingId ? `${apiUrl}/api/admin/mail-settings/${editingId}` : `${apiUrl}/api/admin/mail-settings`;
      const method = editingId ? 'put' : 'post';
      const response = await axios[method](url, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setSuccess(editingId ? 'Profile updated successfully.' : 'Profile added successfully.');
        resetForm();
        fetchProfiles();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to save.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mail profile? This cannot be undone.')) return;
    try {
      const token = getToken();
      const response = await axios.delete(`${apiUrl}/api/admin/mail-settings/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        fetchProfiles();
      } else {
        setError(response.data.message || 'Failed to delete.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting.');
    }
  };

  const handleActivate = async (id) => {
    setError('');
    setSuccess('');
    try {
      const token = getToken();
      const response = await axios.post(`${apiUrl}/api/admin/mail-settings/${id}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setSuccess('Mail profile activated.');
        fetchProfiles();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to activate.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error activating.');
    }
  };

  const handleSendTest = async (id) => {
    if (!testEmailAddress) return;
    setTesting(true);
    setError('');
    setSuccess('');
    try {
      const token = getToken();
      const response = await axios.post(`${apiUrl}/api/admin/mail-settings/${id}/test`, { to: testEmailAddress }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setSuccess(`Test email sent to ${testEmailAddress}.`);
        setTestEmailFor(null);
        setTestEmailAddress('');
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(response.data.message || 'Failed to send test email.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error sending test email.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Mail Settings</span></li>
              </ul>
            </div>
            <button type="button" className="btn btn-main rounded-pill py-9" onClick={() => (showForm ? resetForm() : startAdd())}>
              <i className={`ph ${showForm ? 'ph-x' : 'ph-plus'} me-2`} /> {showForm ? 'Cancel' : 'Add Mail Profile'}
            </button>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          {showForm && (
            <div className="card mb-24">
              <div className="card-header border-bottom border-gray-100">
                <h5 className="mb-0">{editingId ? 'Edit Mail Profile' : 'Add Mail Profile'}</h5>
                <span className="text-13 text-gray-400 d-block mt-4">
                  e.g. a "Default SMTP" profile, or a "Brevo" profile using Brevo's SMTP relay (host <code>smtp-relay.brevo.com</code>, port 587, your Brevo login as username, and an SMTP key as the password).
                </span>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-20">
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Profile Name <span className="text-red-500">*</span></label>
                      <input type="text" className="form-control py-11" value={form.profile_name} onChange={(e) => setField('profile_name', e.target.value)} required disabled={saving} placeholder="e.g. Default SMTP or Brevo" />
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Host <span className="text-red-500">*</span></label>
                      <input type="text" className="form-control py-11" value={form.host} onChange={(e) => setField('host', e.target.value)} required disabled={saving} placeholder="e.g. smtp-relay.brevo.com" />
                    </div>
                    <div className="col-md-3">
                      <label className="h5 mb-8 fw-semibold font-heading">Port <span className="text-red-500">*</span></label>
                      <input type="number" className="form-control py-11" value={form.port} onChange={(e) => setField('port', e.target.value)} required disabled={saving} />
                    </div>
                    <div className="col-md-3 d-flex align-items-end">
                      <div className="form-check pb-11">
                        <input type="checkbox" className="form-check-input" id="secure" checked={form.secure} onChange={(e) => setField('secure', e.target.checked)} disabled={saving} />
                        <label className="form-check-label" htmlFor="secure">Use SSL (port 465)</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">Username <span className="text-red-500">*</span></label>
                      <input type="text" className="form-control py-11" value={form.username} onChange={(e) => setField('username', e.target.value)} required disabled={saving} />
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">
                        Password {editingId ? <span className="text-gray-400 fw-normal text-13">(leave blank to keep current)</span> : <span className="text-red-500">*</span>}
                      </label>
                      <input type="password" className="form-control py-11" value={form.password} onChange={(e) => setField('password', e.target.value)} required={!editingId} disabled={saving} />
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">From Email</label>
                      <input type="email" className="form-control py-11" value={form.from_email} onChange={(e) => setField('from_email', e.target.value)} disabled={saving} placeholder="noreply@ifmedu.in" />
                    </div>
                    <div className="col-md-6">
                      <label className="h5 mb-8 fw-semibold font-heading">From Name</label>
                      <input type="text" className="form-control py-11" value={form.from_name} onChange={(e) => setField('from_name', e.target.value)} disabled={saving} placeholder="IFM Edu Academy" />
                    </div>
                    <div className="col-12 flex-align justify-content-end gap-8">
                      <button type="button" className="btn btn-outline-main rounded-pill py-9" onClick={resetForm}>Cancel</button>
                      <button type="submit" className="btn btn-main rounded-pill py-9" disabled={saving}>
                        {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Profile'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-header border-bottom border-gray-100">
              <h5 className="mb-0">Mail Profiles ({profiles.length})</h5>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : profiles.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No mail profiles yet.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Profile</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Host</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Username</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Status</th>
                        <th className="py-12 px-20 text-13 fw-semibold text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profiles.map((p) => (
                        <React.Fragment key={p.id}>
                          <tr>
                            <td className="py-12 px-20 fw-medium">{p.profile_name}</td>
                            <td className="py-12 px-20 text-14">{p.host}:{p.port}</td>
                            <td className="py-12 px-20 text-14">{p.username}</td>
                            <td className="py-12 px-20">
                              {p.is_active ? (
                                <span className="badge bg-success-100 text-success-600 border-0 py-4 px-12 rounded-pill text-13 fw-medium">Active</span>
                              ) : (
                                <span className="badge bg-gray-100 text-gray-500 border-0 py-4 px-12 rounded-pill text-13 fw-medium">Inactive</span>
                              )}
                            </td>
                            <td className="py-12 px-20">
                              <div className="d-flex align-items-center gap-8 flex-wrap">
                                {!p.is_active && (
                                  <button type="button" className="btn btn-sm btn-outline-main rounded-pill py-4 px-12" onClick={() => handleActivate(p.id)}>
                                    Set Active
                                  </button>
                                )}
                                <button type="button" className="btn btn-sm btn-outline-main rounded-pill py-4 px-12" onClick={() => setTestEmailFor(testEmailFor === p.id ? null : p.id)}>
                                  <i className="ph ph-paper-plane-tilt me-1" /> Test
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-main rounded-pill py-4 px-12" onClick={() => startEdit(p)}>
                                  <i className="ph ph-pencil me-1" /> Edit
                                </button>
                                {!p.is_active && (
                                  <button
                                    type="button"
                                    className="btn btn-sm rounded-pill py-4 px-12"
                                    style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff' }}
                                    onClick={() => handleDelete(p.id)}
                                  >
                                    <i className="ph ph-trash me-1" /> Delete
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {testEmailFor === p.id && (
                            <tr>
                              <td colSpan={5} className="py-12 px-20 bg-gray-50">
                                <div className="d-flex align-items-center gap-8">
                                  <input
                                    type="email"
                                    className="form-control py-9"
                                    style={{ maxWidth: 320 }}
                                    placeholder="Send test email to..."
                                    value={testEmailAddress}
                                    onChange={(e) => setTestEmailAddress(e.target.value)}
                                    disabled={testing}
                                  />
                                  <button type="button" className="btn btn-main rounded-pill py-9" onClick={() => handleSendTest(p.id)} disabled={testing || !testEmailAddress}>
                                    {testing ? 'Sending...' : 'Send'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminMailSettings;
